import { AnalyticsActionType } from '@wppopen/core'
import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useProjects } from 'api/querries/useProjects'
import { useProjectsStatus } from 'api/querries/useProjectsStatus'
import WorkdayIcon from 'assets/icons/workday.svg'
import BigText from 'components/bigText/BigText'
import { Button } from 'components/common/button/Button'
import { Pagination } from 'components/pagination/Pagination'
import ProjectsList, { Sort } from 'components/ProjectsList/ProjectsList'
import ProjectsListFilters, { ProjectsFiltersType } from 'components/projectsListFilters/ProjectsListFilters'
import useDebounce from 'hooks/useDebounce'
import DefaultLayout from 'layout/DefaultLayout'
import { ANALYTICS_EVENTS, trackAnalytics } from 'utils/analytics'

import styles from './BriefOverview.module.scss'

const DEFAULT_PAGE_SIZE = 6

const BriefOverview = () => {
  const [activeFilters, setActiveFilters] = useState<ProjectsFiltersType>()
  const [sort, setSort] = useState<Sort | undefined>()
  const [currentPage, setCurrentPage] = useState(1)
  const [paginationPageSize, setPaginationPageSize] = useState(DEFAULT_PAGE_SIZE)

  const filtersRef = useRef<{
    clearAllFilters?: () => void
    setWorkfrontIdFilter?: (workfrontId?: string) => void
  } | null>(null)

  const debouncedSearch = useDebounce(activeFilters?.search, 800)

  const [query] = useSearchParams()

  const { data: projectsStatus } = useProjectsStatus()

  const { data: projects, isLoading } = useProjects({
    params: {
      sortBy: sort?.orientation && sort?.sortingKey ? [sort.sortingKey] : ['updatedAt'],
      sortDirection: sort?.orientation ? sort.orientation : 'DESC',
      search: debouncedSearch,
      page: currentPage ? currentPage : 1,
      limit: paginationPageSize || undefined,
      targetInMarketDateStart: activeFilters?.targetInMarketDate?.[0],
      targetInMarketDateEnd: activeFilters?.targetInMarketDate?.[1],
      createdAtStart: activeFilters?.dateSubmitted?.[0],
      createdAtEnd: activeFilters?.dateSubmitted?.[1],
      operatingUnit: activeFilters?.operatingUnit,
      uhubProjectStatus: activeFilters?.uhubProjectStatus,
      uhubStatus: activeFilters?.uhubStatus,
      status: activeFilters?.status,
      syncStatus: activeFilters?.syncStatus,
      briefType: activeFilters?.briefType,
      workfrontProjectId: query.get('workfrontProjectId') || undefined,
    },
  })

  const handleSortingChage = (e: Sort) => setSort(e || undefined)

  useEffect(() => {
    if (query.get('workfrontProjectId')) {
      filtersRef?.current?.setWorkfrontIdFilter?.(query.get('workfrontProjectId') || undefined)
    }
  }, [query])

  useEffect(() => trackAnalytics({ type: AnalyticsActionType.page, payload: ANALYTICS_EVENTS.OVERVIEW_PAGE_LOAD }), [])

  return (
    <DefaultLayout className={styles.BriefOverview}>
      <div className={styles.header}>
        <div className={clsx(styles.containerText)}>
          <div className={clsx(styles.wrapper)}>
            <div className={clsx(styles.content)}>
              <BigText className="w-full left-0 -top-[60px] absolute" />
              <div className={clsx('flex mt-[40px] max-w-[1440px] mx-auto justify-between')}>
                <div className="flex flex-col">
                  <div>
                    <div className="relative">
                      <div>
                        <span className={clsx(styles.title)}>
                          Brief Status <br />
                          Overview
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-[34px]">
                    <span className={clsx(styles.subtitle)}>
                      Stay in control! Easily track brief synchronisation between WF and UHUB, <br />
                      catch sync issues at a glance, and keep everything running smoothly.
                    </span>
                  </div>
                </div>
                <div className="flex flex-col">
                  <div className="flex flex-grow justify-end items-end flex-col min-w-fit">
                    <div className="flex items-center">
                      <div className="flex-col mx-2">
                        <Button
                          text="Workfront"
                          iconRight={<img src={WorkdayIcon} alt="icon" />}
                          variant="outlined"
                          className="transition-all tour-communityofpractitioners-visibilitypreferences"
                          onClick={() => {
                            window.open(process.env.REACT_APP_WORKFRONT_URL, '_blank')
                            trackAnalytics({
                              type: AnalyticsActionType.action,
                              payload: ANALYTICS_EVENTS.OVERVIEW_WORKFRONT_BUTTON_CLICKED,
                            })
                          }}
                        />
                      </div>
                      <div className="flex-col mx-2">
                        <Button
                          text="UHUB"
                          iconRight={<img src={WorkdayIcon} alt="icon" />}
                          variant="outlined"
                          className="transition-all tour-communityofpractitioners-visibilitypreferences"
                          onClick={() => {
                            window.open(process.env.REACT_APP_UHUB_URL)
                            trackAnalytics({
                              type: AnalyticsActionType.action,
                              payload: ANALYTICS_EVENTS.OVERVIEW_UHUB_BUTTON_CLICKED,
                            })
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.container}>
        <div className={styles.table}>
          <div>
            <ProjectsListFilters
              ref={filtersRef}
              onChange={(value: Partial<ProjectsFiltersType>) => {
                setCurrentPage(1)
                setPaginationPageSize(DEFAULT_PAGE_SIZE)
                setActiveFilters(value)
              }}
              errorsCount={projectsStatus?.errors}
              warningsCount={projectsStatus?.warnings}
            />
          </div>
          <ProjectsList
            onHeaderClick={handleSortingChage}
            sort={sort}
            headers={[
              { title: '', key: '' },
              {
                title: (
                  <span>
                    Project Name
                    <br /> and Brief Type
                  </span>
                ),
                key: 'name_brief',
                sortable: true,
                sortingKey: 'name',
              },

              {
                title: 'Workfront submission',
                key: 'workfront-status',
                sortable: true,
                sortingKey: 'status',
                group: 'status',

                hideBorder: true,
              },
              {
                title: 'uHub',
                key: 'uhub-status',
                sortable: true,
                sortingKey: 'uhubProjectStatus',
                group: 'uhubStatus',
              },
              {
                title: 'uHub phase',
                key: 'uhub-phase',
                sortable: true,
                sortingKey: 'uhubStatus',
                // group: 'phase',
              },
              { title: 'OU', key: 'ou', sortable: true, sortingKey: 'operatingUnit' },

              {
                title: 'Date submitted',
                key: 'createdAt',
                sortable: true,
                sortingKey: 'createdAt',
              },
              {
                title: 'Target in Market Date',
                key: 'target-market-date',
                sortable: true,
                sortingKey: 'targetInMarketDate',
              },
            ]}
            items={projects?.records}
            className="flex-1"
            isLoading={isLoading}
          />
          <Pagination
            disabled={!projects?.records?.length}
            page={currentPage}
            allPages={Math.ceil((projects?.total || 0) / paginationPageSize)}
            itemsPerPage={paginationPageSize}
            allItems={projects?.total || 0}
            onSelectPage={page => {
              setCurrentPage(page)
            }}
            onSelectItemsPerPage={(value: number) => {
              setPaginationPageSize(value)
              setCurrentPage(1)
            }}
          />
        </div>
      </div>
    </DefaultLayout>
  )
}

export default BriefOverview
